import React, { useEffect, useState } from 'react';

import { TextWrapper } from './styledComponent';

// Typewriter component
interface TypewriterProps {
  strings: string[];
  typingSpeed?: number; // Speed of typing in ms
  deletingSpeed?: number; // Speed of deleting in ms
  delay?: number; // Delay before starting the next string
}

const TypeWriterAnimation: React.FC<TypewriterProps> = ({
  strings,
  typingSpeed = 100,
  deletingSpeed = 50,
  delay = 1000
}) => {
  const [text, setText] = useState('');
  const [index, setIndex] = useState(0); // current string index
  const [isDeleting, setIsDeleting] = useState(false); // whether we are deleting
  const [loop, setLoop] = useState(true); // controls the infinite loop

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (loop) {
      if (isDeleting) {
        // Deleting the text
        timeoutId = setTimeout(() => {
          setText((prev) => prev.slice(0, prev.length - 1)); // Remove the last character
        }, deletingSpeed);
      } else {
        // Typing the text
        if (text.length < strings[index].length) {
          timeoutId = setTimeout(() => {
            setText((prev) => prev + strings[index][text.length]); // Add the next character
          }, typingSpeed);
        } else {
          // Finished typing the string
          timeoutId = setTimeout(() => {
            setIsDeleting(true); // Start deleting after the string is typed
          }, delay);
        }
      }
    } else {
      // Once it's done, restart the cycle
      setLoop(true);
    }

    return () => clearTimeout(timeoutId); // Cleanup timeout when effect is rerun
  }, [
    text,
    index,
    isDeleting,
    strings,
    typingSpeed,
    deletingSpeed,
    delay,
    loop
  ]);

  useEffect(() => {
    if (text === '' && isDeleting) {
      // When the text is fully deleted, move to the next string
      setIsDeleting(false);
      setIndex((prev) => (prev + 1) % strings.length); // Go to next string
    }
  }, [text, isDeleting, strings.length]);

  return <TextWrapper width={text.length * 16}>{text}</TextWrapper>; // Assuming 16px width per character
};

export { TypeWriterAnimation };
